import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Empty,
  Form,
  Input,
  Modal,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { CopyOutlined, PlusOutlined, ReloadOutlined, SaveOutlined } from '@ant-design/icons';
import { getAccessTokenApi } from 'api/auth';
import {
  getTenantTextsByDocumentTypeApi,
  createTenantTextApi,
  updateTenantTextApi,
} from 'api/tenantText';

const { Text } = Typography;

// Panel lateral del TemplateEditor para editar tenant_texts del tenant activo,
// filtrados por el documentType actual. Cada fila se puede editar inline
// (Input.TextArea + botón Guardar). También expone el botón "Insertar {key}"
// que copia la variable al portapapeles y dispara el callback onKeyInsert si
// el padre lo provee (ej. para focar el SectionEditor y pegar).
//
// Auth: usa BASE_PATH_SERVER + token via getAccessTokenApi. Requiere tenantId
// válido (el panel no se monta si es falsy — decisión del padre).
function TenantTextsPanel({ tenantId, documentType, onKeyInsert }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [savingKey, setSavingKey] = useState(null);
  const [drafts, setDrafts] = useState({}); // { [keyName]: textValue }
  const [addModalOpen, setAddModalOpen] = useState(false);

  const load = useCallback(async () => {
    if (!tenantId || !documentType) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getAccessTokenApi();
      const result = await getTenantTextsByDocumentTypeApi(token, tenantId, documentType);
      if (result?.status === 'client') throw new Error(result.message);
      const safe = Array.isArray(result) ? result : [];
      setRows(safe);
      // Resetea los drafts al valor persistido
      const nextDrafts = {};
      for (const r of safe) nextDrafts[r.keyName] = r.textValue ?? '';
      setDrafts(nextDrafts);
    } catch (err) {
      setError(err.message || 'Error al cargar textos del tenant');
    } finally {
      setLoading(false);
    }
  }, [tenantId, documentType]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSaveRow = async (row) => {
    const nextValue = drafts[row.keyName] ?? '';
    if (nextValue === row.textValue) return;
    setSavingKey(row.keyName);
    try {
      const token = await getAccessTokenApi();
      const result = await updateTenantTextApi(token, tenantId, row.id, {
        textValue: nextValue,
      });
      if (result?.status === 'client') throw new Error(result.message);
      message.success(`"${row.keyName}" guardado`);
      await load();
    } catch (err) {
      message.error(`Error al guardar: ${err.message}`);
    } finally {
      setSavingKey(null);
    }
  };

  const handleInsert = (row) => {
    const text = `{${row.keyName}}`;
    navigator.clipboard?.writeText(text);
    if (typeof onKeyInsert === 'function') onKeyInsert(text);
    message.success(`${text} copiado`);
  };

  if (!tenantId) return null;

  return (
    <Card
      size="small"
      title={
        <Space>
          <span>Textos del tenant</span>
          <Tag color="blue">{documentType}</Tag>
        </Space>
      }
      extra={
        <Space>
          <Tooltip title="Recargar">
            <Button size="small" icon={<ReloadOutlined />} onClick={load} loading={loading} />
          </Tooltip>
          <Tooltip title="Agregar key">
            <Button
              size="small"
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setAddModalOpen(true)}
            />
          </Tooltip>
        </Space>
      }
      style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      styles={{ body: { overflowY: 'auto', flex: 1, padding: 12 } }}
    >
      {error ? <Alert type="error" message={error} showIcon style={{ marginBottom: 8 }} /> : null}

      {loading && rows.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin />
        </div>
      ) : rows.length === 0 ? (
        <Empty
          description={
            <Text type="secondary">
              No hay textos para <code>{documentType}</code>. Agregá con el botón +.
            </Text>
          }
        />
      ) : (
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          {rows.map((row) => {
            const draft = drafts[row.keyName] ?? '';
            const dirty = draft !== row.textValue;
            return (
              <div
                key={row.id}
                style={{
                  border: '1px solid #f0f0f0',
                  borderRadius: 6,
                  padding: 8,
                  background: '#fafafa',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 4,
                  }}
                >
                  <Text code style={{ fontSize: 12 }}>
                    {row.keyName}
                  </Text>
                  <Tooltip title="Copiar {key} al portapapeles">
                    <Button
                      type="text"
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={() => handleInsert(row)}
                    />
                  </Tooltip>
                </div>
                <Input.TextArea
                  value={draft}
                  autoSize={{ minRows: 1, maxRows: 5 }}
                  onChange={(e) => setDrafts((d) => ({ ...d, [row.keyName]: e.target.value }))}
                />
                {dirty ? (
                  <div style={{ marginTop: 4, textAlign: 'right' }}>
                    <Button
                      size="small"
                      type="primary"
                      icon={<SaveOutlined />}
                      loading={savingKey === row.keyName}
                      onClick={() => handleSaveRow(row)}
                    >
                      Guardar
                    </Button>
                  </div>
                ) : null}
              </div>
            );
          })}
        </Space>
      )}

      <AddKeyModal
        open={addModalOpen}
        tenantId={tenantId}
        documentType={documentType}
        onClose={() => setAddModalOpen(false)}
        onSaved={async () => {
          setAddModalOpen(false);
          await load();
        }}
      />
    </Card>
  );
}

function AddKeyModal({ open, tenantId, documentType, onClose, onSaved }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) form.resetFields();
  }, [open, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const token = await getAccessTokenApi();
      const result = await createTenantTextApi(token, {
        tenantId,
        keyName: values.keyName.trim(),
        textValue: values.textValue ?? '',
        documentType,
      });
      if (result?.status === 'client') throw new Error(result.message);
      message.success('Key creada');
      onSaved?.();
    } catch (err) {
      if (err?.errorFields) return; // validación del form
      message.error(`Error al crear: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Agregar key de tenant_text"
      open={open}
      onCancel={onClose}
      onOk={handleSave}
      okText="Crear"
      confirmLoading={saving}
      destroyOnHidden
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="keyName"
          label="Nombre de la key (snake_case)"
          rules={[
            { required: true, message: 'Requerido' },
            {
              pattern: /^[a-zA-Z][a-zA-Z0-9_.]*$/,
              message: 'Solo letras, números, _ y . (debe empezar con letra)',
            },
          ]}
        >
          <Input placeholder="ej: closing_paragraph" autoFocus />
        </Form.Item>
        <Form.Item name="textValue" label="Valor inicial (opcional)">
          <Input.TextArea rows={3} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default TenantTextsPanel;
